import OSS from 'ali-oss';

import { TIMEOUT } from '@/utils/const';
import {
  ErrorType,
  ITokenData,
  IUploaderProps,
  IUploadInstanceOptions,
} from '@/utils/Uploader/type';

import { UploaderBase } from './base';

export class OssUploader implements UploaderBase {
  TIMEOUT: number = TIMEOUT;
  uploadId = '';
  public client: OSS | null = null;
  private checkpoint: OSS.Checkpoint | undefined;
  private options: IUploaderProps | null = null;

  constructor(private callbackOptions: IUploadInstanceOptions) {}

  // 上传
  public async upload(options: IUploaderProps): Promise<string | null> {
    return new Promise(async (resolve) => {
      // 回调函数
      const { onError, onBefore } = this.callbackOptions;

      const { tokenData, file, fileUid } = options;
      this.options = options;

      const fileId = fileUid || '';
      const start = new Date().getTime();
      try {
        // 获取OSS-Options参数
        const ossOptions = this.getOssOptions(tokenData);
        if (!ossOptions) {
          const err = new Error('Get OSS Options Error');
          err.name = ErrorType.Other;
          throw err;
        }
        // 获取client
        this.client = await this.getClient(ossOptions);
        if (!this.client) {
          const err = new Error('Get OSS Client Error');
          err.name = ErrorType.Other;
          throw err;
        }
        try {
          onBefore && (await onBefore({ file }));
        } catch (error: any) {
          console.error(error);
          resolve(null);
          return;
        }

        const res = await this.multipartUpload(options);
        resolve(res);
      } catch (error: any) {
        const err = new Error(error?.message || '');
        err.name = error?.name || ErrorType.Other;
        onError &&
          onError(
            {
              error: err,
              name: error?.name || ErrorType.Other,
              message: error?.message || '',
              start,
              end: new Date().getTime(),
            },
            fileId,
            this,
          );
        resolve(null);
      }
    });
  }

  // 批量上传
  private async multipartUpload(options: IUploaderProps) {
    const { tokenData } = options;
    if (this.client && tokenData) {
      const { onProgress, onSuccess, onStart } = this.callbackOptions;
      const { file } = options;
      const { filePath, accessUrl } = tokenData;
      const that = this;
      const start = new Date().getTime();
      onStart && onStart(start);
      const res = await this.client.multipartUpload(filePath, file, {
        parallel: 4,
        partSize: 1024 * 1024 * 4,
        async progress(percent: number, checkPoint: OSS.Checkpoint) {
          if (checkPoint) that.uploadId = checkPoint.uploadId;
          this.checkpoint = checkPoint;
          onProgress &&
            onProgress({ progress: percent, status: percent >= 1, url: accessUrl });
          if (percent === 1) {
            onSuccess &&
              (await onSuccess(accessUrl, {
                start,
                end: new Date().getTime(),
              }));
          }
        },
        checkpoint: this.checkpoint,
        headers: { 'cache-control': '', 'Content-Disposition': 'attachment' },
      });
      if (res) return tokenData?.accessUrl || '';
      return null;
    }
    return null;
  }

  // 初始化
  public async initMultipartUpload() {
    if (this.client && this.options) {
      const { tokenData } = this.options;
      const { filePath } = tokenData || {};
      return await this.client.initMultipartUpload(filePath);
    }
    return null;
  }

  // 取消上传
  public async cancel() {
    if (this.client && this.options && this.uploadId) {
      const { tokenData } = this.options;
      const { filePath } = tokenData || {};
      const result: any = await this.client.abortMultipartUpload(filePath, this.uploadId);
      if (result && String(result?.res?.status).startsWith('2')) {
        const { onCancel } = this.callbackOptions;
        const fileId = (this.options && this.options.fileUid) || '';
        onCancel && (await onCancel(fileId));
        return result;
      }
    }
    return null;
  }

  // 重试
  public async retry() {
    const options = this.options;
    if (options) {
      const { fileUid } = options;
      const start = new Date().getTime();
      try {
        return await this.multipartUpload(options);
      } catch (error: any) {
        console.error(error);
        const { onError } = this.callbackOptions;
        const newErr = new Error(error?.message || '');
        newErr.name = error?.name || ErrorType.Other;
        const err = {
          name: error.name || ErrorType.Other,
          message: error.message || '',
          start,
          end: new Date().getTime(),
          error: newErr,
        };
        onError && onError(err, fileUid || '', this);
        return null;
      }
    }
    return null;
  }

  // 获取oss-option
  private getOssOptions(tokenData: ITokenData): OSS.Options | null {
    if (!tokenData) return null;

    const { uploadHost, bucket, region, accessKey, accessSecret, securityToken } =
      tokenData;

    const { host } = new URL(uploadHost);

    const { timeout } = this.callbackOptions;

    return {
      timeout: timeout || TIMEOUT,
      endpoint: `https://${bucket}.${host}`,
      region,
      accessKeyId: accessKey,
      accessKeySecret: accessSecret,
      stsToken: securityToken,
      bucket,
      cname: true,
    };
  }

  public async getClient(params: OSS.Options): Promise<OSS> {
    console.log('oss params1123123');
    const OSS = (await import('ali-oss')).default;
    return new OSS(params);
  }
}
